import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/shared/Card';
import { Badge } from '../components/shared/Badge';
import { Button } from '../components/shared/Button';
import { VehicleBanner } from '../components/tire/VehicleBanner';
import { ReportHeader } from '../components/tire/ReportHeader';
import { PrintButton } from '../components/tire/PrintButton';
import { api } from '../lib/api';
import type { Inspection } from '../types';
import { getTirePositionLabel, getWearPatternLabel } from '../lib/utils';

export const InspectionDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [inspection, setInspection] = useState<Inspection | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadInspection = async () => {
      if (id) {
        try {
          const data = await api.getInspection(id);
          setInspection(data);
        } catch (error) {
          console.error('Failed to load inspection:', error);
          setInspection(null);
        } finally {
          setLoading(false);
        }
      }
    };

    loadInspection();
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-12 h-12 border-4 border-brand-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!inspection) {
    return (
      <Card>
        <div className="text-center py-12">
          <svg
            className="w-16 h-16 text-gray-300 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Inspection Not Found
          </h2>
          <p className="text-gray-600 mb-6">
            The inspection you're looking for doesn't exist.
          </p>
          <Button onClick={() => navigate('/inspections')}>
            Back to Inspections
          </Button>
        </div>
      </Card>
    );
  }

  const avgTread = (
    inspection.tires.reduce((sum, tire) => sum + tire.tread_mm, 0) /
    inspection.tires.length
  ).toFixed(1);

  // Check if any tires need replacement
  const badTires = inspection.tires.filter(
    (tire) => tire.status === 'replace_soon' || tire.status === 'unsafe'
  );
  const needsReplacement = badTires.length > 0;

  return (
    <div className="space-y-8">
      {/* Action Buttons (no-print) */}
      <div className="flex items-center justify-between no-print">
        <Button variant="ghost" onClick={() => navigate('/inspections')}>
          ← Back to Inspections
        </Button>
        <div className="flex gap-3">
          <PrintButton />
          <Button variant="outline" onClick={() => navigate('/inspect')}>
            New Inspection
          </Button>
        </div>
      </div>

      {/* Report Content */}
      <div className="bg-white p-8 rounded-lg shadow-sm print:shadow-none">
        <ReportHeader
          inspectionId={inspection.id}
          createdAt={inspection.createdAt}
        />

        <VehicleBanner vehicle={inspection.vehicle} className="mb-6" />

        {/* Overall Status */}
        <Card className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Overall Status
              </h2>
              <p className="text-sm text-gray-600">
                Average Tread Depth: {avgTread} mm
              </p>
            </div>
            <Badge status={inspection.overallStatus} size="lg" />
          </div>
        </Card>

        {/* Tire Replacement Recommendation */}
        {needsReplacement && (
          <Card className="mb-6 border-l-4 border-red-500 bg-red-50">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <svg
                  className="w-8 h-8 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-red-900 mb-2">
                  Tire Replacement Required
                </h3>
                <p className="text-sm text-red-800 mb-3">
                  {badTires.length} tire{badTires.length > 1 ? 's' : ''} need
                  immediate replacement for your safety:
                </p>
                <ul className="text-sm text-red-800 mb-4 space-y-1">
                  {badTires.map((tire) => (
                    <li key={tire.position} className="flex items-center gap-2">
                      <span className="font-semibold">
                        {getTirePositionLabel(tire.position)}:
                      </span>
                      <span>
                        {tire.tread_mm.toFixed(1)} mm tread depth
                        {tire.status === 'unsafe' && (
                          <span className="ml-1 text-red-900 font-bold">
                            (UNSAFE)
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap gap-3">
                  <a
                    href="https://www.tirerack.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 transition-colors"
                  >
                    Shop at Tire Rack
                    <svg
                      className="w-4 h-4 ml-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                      />
                    </svg>
                  </a>
                  <a
                    href="https://www.discounttire.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 transition-colors"
                  >
                    Shop at Discount Tire
                    <svg
                      className="w-4 h-4 ml-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                      />
                    </svg>
                  </a>
                  <a
                    href="https://www.google.com/maps/search/tire+shop+near+me"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 bg-white text-red-600 text-sm font-medium rounded-md border border-red-600 hover:bg-red-50 transition-colors"
                  >
                    Find Local Tire Shops
                    <svg
                      className="w-4 h-4 ml-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Tire Details Table */}
        <Card className="mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Tire Details
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">
                    Position
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">
                    Tread Depth
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">
                    Wear Pattern
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">
                    Age
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">
                    DOT Code
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {inspection.tires.map((tire) => (
                  <tr key={tire.position} className="border-b border-gray-100">
                    <td className="py-3 px-4 font-medium text-gray-900">
                      {getTirePositionLabel(tire.position)}
                    </td>
                    <td className="py-3 px-4 text-gray-900">
                      {tire.tread_mm.toFixed(1)} mm
                    </td>
                    <td className="py-3 px-4 text-gray-600">
                      {getWearPatternLabel(tire.wear_pattern)}
                    </td>
                    <td className="py-3 px-4 text-gray-600">
                      {tire.age_years.toFixed(1)} years
                    </td>
                    <td className="py-3 px-4 text-xs font-mono text-gray-600">
                      {tire.dot_code}
                    </td>
                    <td className="py-3 px-4">
                      <Badge status={tire.status} size="sm" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Recommendations */}
        <Card className="mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Recommendations
          </h2>
          <ul className="space-y-2">
            {inspection.recommendations.map((rec, idx) => (
              <li key={idx} className="flex items-start gap-2">
                <span className="text-brand-600 mt-1 font-bold">•</span>
                <span className="text-gray-700">{rec}</span>
              </li>
            ))}
          </ul>
        </Card>

        {/* Legend */}
        <Card>
          <h3 className="text-sm font-semibold text-gray-900 mb-3">
            Safety Thresholds
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs text-gray-600">
            <div>
              <span className="font-semibold">Tread Depth:</span>
              <ul className="mt-1 space-y-0.5 ml-4">
                <li>• {">"} 4.0 mm: OK</li>
                <li>• 3.0 - 4.0 mm: Watch</li>
                <li>• 2.0 - 3.0 mm: Replace Soon</li>
                <li>• {"<"} 2.0 mm: Unsafe</li>
              </ul>
            </div>
            <div>
              <span className="font-semibold">Tire Age:</span>
              <ul className="mt-1 space-y-0.5 ml-4">
                <li>• {"<"} 5 years: OK</li>
                <li>• 5 - 6 years: Watch</li>
                <li>• {">"} 6 years: Replace</li>
              </ul>
            </div>
          </div>
        </Card>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-gray-200 text-center text-xs text-gray-500">
          <p>
            This report was generated by TreadAI using simulated tire analysis.
          </p>
          <p className="mt-1">
            For actual vehicle safety decisions, consult a certified tire
            professional.
          </p>
        </div>
      </div>
    </div>
  );
};
